import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useParams, useSearchParams } from 'react-router-dom';
import './searchObject.scss'
import AIPanel from "../../../components/AIChat/AIPanel";
import { TableControl } from 'spark-core-dx/components';
import { useCoreContext, useCoreStateContext } from 'spark-core-dx/contexts';
import ConvokeWarehouseViewManager from '../../../managers/convoke/ConvokeWarehouseViewManager';

const getPanelText = (value) => String(value ?? "").trim();

const parsePanelParams = (value) => {
    if (!value) return {};
    if (typeof value === "object" && !Array.isArray(value)) return value;
    if (typeof value === "string") {
        try {
            const parsed = JSON.parse(value);
            return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
        } catch {
            return {};
        }
    }
    return {};
};

const readFirstPanelParam = (params, keys) => {
    for (const key of keys) {
        const value = getPanelText(params?.[key]);
        if (value) return value;
    }
    return "";
};

const getObjectPropCI = (obj, ...names) => {
    if (!obj || typeof obj !== "object") return undefined;
    const lookup = new Map(Object.keys(obj).map((key) => [String(key).toLowerCase(), key]));
    for (const name of names) {
        const key = lookup.get(String(name).toLowerCase());
        if (key !== undefined) return obj[key];
    }
    return undefined;
};

const parseMaybeJsonValue = (value) => {
    if (typeof value !== "string") return value;
    const trimmed = value.trim();
    if (!trimmed) return value;
    try {
        return JSON.parse(trimmed);
    } catch {
        return value;
    }
};

const looksLikeFilterQuery = (value) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) return false;
    return getObjectPropCI(
        value,
        "WarehouseViewId",
        "warehouseViewId",
        "RootFilterNode",
        "rootFilterNode",
        "SelectColumns",
        "selectColumns",
        "Skip",
        "skip",
        "Take",
        "take",
        "SortBy",
        "sortBy"
    ) !== undefined;
};

const resolveFilterQueryPayload = (payload, seen = new Set()) => {
    const parsed = parseMaybeJsonValue(payload);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
    if (seen.has(parsed)) return null;
    seen.add(parsed);

    if (looksLikeFilterQuery(parsed)) return parsed;

    const args = getObjectPropCI(parsed, "args", "Args");
    const metadata = getObjectPropCI(parsed, "metadata", "Metadata");
    const candidates = [
        getObjectPropCI(metadata, "query", "Query"),
        getObjectPropCI(args, "query", "Query"),
        getObjectPropCI(args, "output", "Output"),
        getObjectPropCI(parsed, "query", "Query"),
        getObjectPropCI(parsed, "generatedQuery", "GeneratedQuery"),
        getObjectPropCI(parsed, "value", "Value"),
        getObjectPropCI(parsed, "output", "Output")
    ];

    for (const candidate of candidates) {
        const resolved = resolveFilterQueryPayload(candidate, seen);
        if (resolved) return resolved;
    }

    return null;
};

const resolvePanelFilterQuery = (item, value, warehouseViewId) => {
    const candidates = [
        value,
        item?.GeneratedQuery,
        item?.generatedQuery,
        item?.Value,
        item?.value,
        item?.Output,
        item?.output,
        item
    ];

    for (const candidate of candidates) {
        const resolved = resolveFilterQueryPayload(candidate);
        if (resolved) {
            const existingId = getObjectPropCI(resolved, "WarehouseViewId", "warehouseViewId");
            return {
                ...resolved,
                WarehouseViewId: existingId || warehouseViewId
            };
        }
    }

    return null;
};

const firstMetadataItem = (metadata) => {
    if (Array.isArray(metadata)) return metadata.first?.() ?? metadata[0];
    return metadata;
};

const toTextList = (value) => {
    if (Array.isArray(value)) return value.map((entry) => String(entry ?? "").trim()).filter(Boolean);
    return [];
};

const SearchWarehouseView = ({
    panelMode = false,
    panelOpen = true,
    panelId = null,
    panelReferenceId = null,
    panelSessionId = null,
    panelParams = null,
    onPanelClose = null
}) => {
    const [data, setData] = useState(null);
    const [fieldOverrides, setFieldOverrides] = useState(null);
    const coreContext = useCoreContext();
    const coreState = useCoreStateContext();
    const { warehouseViewId: routeWarehouseViewId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const resolvedPanelParams = useMemo(() => parsePanelParams(panelParams), [panelParams]);
    const warehouseViewId = panelMode
        ? (getPanelText(panelId) || getPanelText(searchParams.get("aiPanelId")) || routeWarehouseViewId)
        : routeWarehouseViewId;
    const wId = readFirstPanelParam(resolvedPanelParams, ["wId", "wid", "warehouseId", "WarehouseId"])
        || getPanelText(searchParams.get('wId'));
    const wdId = readFirstPanelParam(resolvedPanelParams, ["wdId", "wdid", "warehouseDeploymentId", "WarehouseDeploymentId"])
        || getPanelText(panelReferenceId)
        || getPanelText(searchParams.get(panelMode ? "aiPanelReferenceId" : 'wdId'));
    const panelSid = getPanelText(panelSessionId) || getPanelText(searchParams.get("aiPanelSessionId"));
    const profileReady = Boolean(coreContext?.Profile?.ClientId && coreContext?.Profile?.AppId);
    const warehouseViewMgr = useMemo(() => {
        if (!warehouseViewId || !profileReady) return null;
        return new ConvokeWarehouseViewManager(warehouseViewId, coreContext, coreState, "SearchWarehouseView", true);
    }, [coreContext, coreState, profileReady, warehouseViewId]);
    const loadingState = warehouseViewMgr?.loading?.All ?? warehouseViewMgr?.loadingSearch ?? false;
    const [initialSearch, setInitialSearch] = useState(false);
    const pageRef = useRef(null);
    const tableData = data ?? warehouseViewMgr?.filteredData ?? [];
    const searchError = getPanelText(warehouseViewMgr?.searchError);
    const selectedChatSession = useMemo(
        () => panelMode && panelSid ? { _id: panelSid } : null,
        [panelMode, panelSid]
    );
    const tableMetadata = useMemo(() => {
        const metadataList = warehouseViewMgr?.uiMetadata;
        if (!Array.isArray(metadataList) || metadataList.length === 0) return metadataList;

        const deepCopy = (value) => typeof structuredClone === "function" ? structuredClone(value) : JSON.parse(JSON.stringify(value));
        return metadataList.map((metadataItem) => {
            const nextMetadata = deepCopy(metadataItem);
            const fields = Array.isArray(nextMetadata?.Fields) ? nextMetadata.Fields : [];
            nextMetadata.ServerFilterSort = true;
            if (!Array.isArray(nextMetadata.HasPaging) || nextMetadata.HasPaging.length === 0) {
                nextMetadata.HasPaging = [50, 100, 250];
            }

            const activeFields = fields.filter(field => field?.IsActive !== false && field?.IsDeleted !== true && !field?.SubUIItemId);
            const visibleFields = activeFields.some(field => field?.ShowOnGrid)
                ? activeFields
                : activeFields.map((field, index) => ({
                    ...field,
                    ShowOnGrid: index < 8
                }));
            nextMetadata.Fields = visibleFields;
            return nextMetadata;
        });
    }, [warehouseViewMgr?.uiMetadata]);

    useEffect(() => {
        if (!warehouseViewMgr || !warehouseViewId || !profileReady) return;

        if (!data && tableMetadata && !initialSearch) {
            setInitialSearch(true);
            const metadata = firstMetadataItem(tableMetadata);
            const take = metadata?.ProfilePreferences?.HasPaging?.first?.()
                ?? metadata?.ProfilePreferences?.HasPaging?.[0]
                ?? metadata?.HasPaging?.first?.()
                ?? metadata?.HasPaging?.[0]
                ?? 50000;
            warehouseViewMgr.search({
                id: warehouseViewId,
                queryParams: { search: true, ...(wId ? { wId } : {}), ...(wdId ? { wdId } : {}) },
                forceUpdate: true,
                take
            });
        } else if (warehouseViewMgr && !warehouseViewMgr?.uiMetadata && typeof warehouseViewMgr?.LoadMetadata === "function") {
            warehouseViewMgr.LoadMetadata();
        }
    }, [data, initialSearch, profileReady, tableMetadata, warehouseViewId, warehouseViewMgr, wId, wdId]);

    useEffect(() => {
        if (!loadingState && warehouseViewMgr) {
            setData(warehouseViewMgr?.filteredData);
        }
    }, [loadingState, warehouseViewMgr, warehouseViewMgr?.filteredData]);

    const onServerSideQuery = async (itemQuery) => {
        if (!warehouseViewMgr) {
            return { Items: [], TotalItemCount: 0 };
        }

        const queryParams = { search: true, ...(wId ? { wId } : {}), ...(wdId ? { wdId } : {}) };
        return await warehouseViewMgr.search({ id: warehouseViewId, queryParams, searchModel: itemQuery, forceUpdate: true });
    };

    const onSessionChange = (sessionId) => {
        if (!panelMode) return;
        const nextParams = new URLSearchParams(searchParams);
        const normalizedSessionId = getPanelText(sessionId);
        if (normalizedSessionId) nextParams.set("aiPanelSessionId", normalizedSessionId);
        else nextParams.delete("aiPanelSessionId");
        setSearchParams(nextParams);
    };

    const onUpdatePanel = async (UICommand, item, value) => {
        if (UICommand !== "filter") return;

        const filterQuery = resolvePanelFilterQuery(item, value, warehouseViewId);
        if (!filterQuery) return;

        const selectColumns = toTextList(getObjectPropCI(filterQuery, "SelectColumns", "selectColumns"));
        const deepCopy = v => typeof structuredClone === "function" ? structuredClone(v) : JSON.parse(JSON.stringify(v));
        const metadataItem = firstMetadataItem(warehouseViewMgr?.uiMetadata);
        const metadataFields = Array.isArray(metadataItem?.Fields) ? metadataItem.Fields : [];
        if (selectColumns.length > 0 && metadataFields.length > 0) {
            const allowed = new Set(selectColumns.map(s => s.toLowerCase()));
            const nextOverrides = metadataFields.map(deepCopy).map((field) => {
                const name = (field?.FieldName ?? "").toString().toLowerCase();
                if (!allowed.has(name)) {
                    field.ShowOnGrid = false;
                    field.IsActive = false;
                    field.IsDeleted = true;
                }
                return field;
            });
            setFieldOverrides(nextOverrides);
        }

        const wid = getObjectPropCI(filterQuery, "warehouseId", "WarehouseId", "wid", "wId") ?? wId;
        const wdid = getObjectPropCI(filterQuery, "warehouseDeploymentId", "WarehouseDeploymentId", "wdid", "wdId") ?? wdId;
        if (!warehouseViewMgr) return;

        const result = await warehouseViewMgr.search({
            id: getObjectPropCI(filterQuery, "WarehouseViewId", "warehouseViewId") ?? warehouseViewId,
            searchModel: filterQuery,
            queryParams: { search: true, ...(wid ? { wId: wid } : {}), ...(wdid ? { wdId: wdid } : {}) },
            forceUpdate: true
        });
        if (Array.isArray(result?.Items)) setData(result.Items);
    };

    return (
        <AIPanel
            childrenRef={pageRef}
            open={panelOpen}
            presentation={panelMode ? "slideout" : "inline"}
            onUpdatePanel={onUpdatePanel}
            onSessionChange={panelMode ? onSessionChange : undefined}
            session={selectedChatSession}
            mapToClass={'core-layout'}
            params={{ wdId, wId }}
            referenceId={warehouseViewId}
            referenceName={"warehouseView"}
            operationName={"AIQuestionWarehouseView"}
            directModeOnly={true}
            onCancel={() => {
                if (panelMode && typeof onPanelClose === "function") onPanelClose();
            }}
        >
            <div className="ai-panel__table" ref={pageRef}>
                {searchError ? (
                    <div className="search-object-query-error" role="alert">
                        {searchError}
                    </div>
                ) : null}
                <TableControl
                    data={tableData}
                    className={"search-object-table"}
                    enableMultiSelect={false}
                    defaultNumRows={50}
                    headerFeatures={["title", "search", "filter", "tableStyles", "columnSettings"]}
                    onServerSideQuery={onServerSideQuery}
                    metadata={tableMetadata}
                    loadingState={loadingState}
                    filters={warehouseViewMgr?.filters}
                    mgr={warehouseViewMgr}
                    fieldOverrides={fieldOverrides}
                    enableSidePanelEdit={true}
                />
            </div>
        </AIPanel>
    );
};

export default SearchWarehouseView;
